import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, ButtonGroup, Card, Form, Spinner } from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { apiClient } from "../../../api/client";
import { notifyError } from "../../../components/AppToast";
import { useCancelClass } from "../hooks/useCancelClass";
import { useBookClass } from "../hooks/useBookClass";
import { useCalendar } from "../hooks/useCalendar";
import { useStudentBalance } from "../hooks/useStudentBalance";
import { formatTime12h } from "../../../utils/formatTime";
import { ClassesCalendar } from "../../../components/ClassesCalendar";
import { AddToCalendar } from "../../../components/AddToCalendar";
import { TopicInfoTrigger } from "../../../components/TopicInfoTrigger";
import { LevelBadge } from "../../../components/LevelBadge";
import { TopicRepeatAlert } from "../components/TopicRepeatAlert";

function useMyClasses() {
  return useQuery({
    queryKey: ["student", "my-classes"],
    queryFn: async () => (await apiClient.get("/api/v1/attendances/")).data,
  });
}

const STATES = [
  { key: "available", label: "Disponible", bg: "secondary", color: "#6c757d" },
  { key: "booked", label: "Agendada", bg: "primary", color: "#1f4e79" },
  { key: "attended", label: "Asistida", bg: "success", color: "#1f9d6b" },
  { key: "missed", label: "No asistida", bg: "danger", color: "#c1121f" },
];
const STATE_MAP = Object.fromEntries(STATES.map((s) => [s.key, s]));

const VIEWS = [
  { key: "combined", label: "Combinado", icon: "mdi-view-split-vertical" },
  { key: "cards", label: "Tarjetas", icon: "mdi-view-grid" },
  { key: "calendar", label: "Calendario", icon: "mdi-calendar-month-outline" },
];

function ScoreStars({ score }) {
  return (
    <span aria-label={`Nota ${score} de 5`} title={`Nota ${score} de 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} style={{ color: n <= score ? "#FFC107" : "#d0d0d0" }}>★</span>
      ))}
    </span>
  );
}

function ClassItemCard({
  item,
  onBook,
  onCancel,
  cancelling,
  availableHours,
  colClassName = "col-12 col-sm-6 col-xl-4 mb-3",
}) {
  const st = STATE_MAP[item.state] ?? { label: item.state, bg: "secondary" };
  const exceeds =
    item.state === "available" && item.duration != null && item.duration > availableHours;

  return (
    <div className={colClassName}>
      <Card className={`h-100 shadow-sm border-start border-4 border-${st.bg}`}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
            <div style={{ minWidth: 0 }}>
              <div className="fw-semibold d-flex align-items-center gap-1">
                {item.hasRepeats && (
                  <i
                    className="mdi mdi-repeat text-warning"
                    title="Repite un tema que ya viste"
                    aria-label="Repite un tema que ya viste"
                  />
                )}
                <span>{item.title}</span>
                <TopicInfoTrigger topics={item.topicsDetail} />
              </div>
              <div className="text-muted small">
                {dayjs(item.date).format("ddd D MMM")}
                {item.time ? ` · ${formatTime12h(item.time)}` : ""}
                {item.duration ? ` · ${item.duration} h` : ""}
              </div>
              {item.location && <div className="text-muted small">{item.location}</div>}
              {item.teacher && <div className="text-muted small">Docente: {item.teacher}</div>}
            </div>
            <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
              <Badge bg={st.bg}>{st.label}</Badge>
              {item.level && (
                <LevelBadge level={item.level} comparison={item.levelComparison} />
              )}
            </div>
          </div>

          {item.state === "attended" && (item.score != null || item.hours != null) && (
            <div className="d-flex align-items-center gap-3 flex-wrap mt-2 pt-2 border-top small">
              {item.score != null && (
                <span className="d-flex align-items-center gap-2">
                  <span className="text-muted">Calificación:</span>
                  <ScoreStars score={item.score} />
                </span>
              )}
              {item.hours != null && (
                <span className="text-muted">Horas: <strong>{item.hours} h</strong></span>
              )}
            </div>
          )}

          {item.state === "available" && exceeds && (
            <div className="text-danger small mt-1">
              <i className="mdi mdi-alert-outline me-1" aria-hidden="true" />
              Excede tu saldo ({availableHours} h disponibles)
            </div>
          )}

          {(item.state === "available" || item.state === "booked") && (
            <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
              {item.state === "available" ? (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={exceeds}
                  title={exceeds ? "No tienes horas suficientes para agendar" : undefined}
                  onClick={() => onBook(item.planningId)}
                >
                  Agendar
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={cancelling === item.attendanceId}
                    onClick={() => onCancel(item.attendanceId)}
                  >
                    {cancelling === item.attendanceId ? "Cancelando…" : "Cancelar"}
                  </Button>
                  <AddToCalendar
                    title={item.title}
                    date={item.date}
                    timeStart={item.time}
                    duration={item.duration}
                    location={item.location}
                    uid={`clase-${item.attendanceId}`}
                    size="sm"
                  />
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

/**
 * Mis clases (estudiante) unificado: Disponibles + Agendadas + Asistidas (+ No
 * asistida), en vistas Combinado / Tarjetas / Calendario, con filtros por estado,
 * sede y tema (E4), saldo de horas contextual (E5) y aviso de tema repetido (E6).
 */
function MyClassesPage() {
  const qc = useQueryClient();
  const from = dayjs().format("YYYY-MM-DD");
  const to = dayjs().add(45, "day").format("YYYY-MM-DD");

  const { data: mine = [], isLoading: l1, isError: e1 } = useMyClasses();
  const { data: avail = [], isLoading: l2, isError: e2 } = useCalendar({ from, to });
  const { data: balance } = useStudentBalance();
  const bookMutation = useBookClass();
  const cancelMutation = useCancelClass();

  const availableHours = Number(balance?.hours_balance ?? 0);
  const absenceHours = Number(balance?.hours_absence ?? 0);

  const [view, setView] = useState("combined");
  const [statusOn, setStatusOn] = useState({
    available: true, booked: true, attended: false, missed: false,
  });
  const [sedeFilter, setSedeFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [pendingEvent, setPendingEvent] = useState(null);
  const [checking, setChecking] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["student", "my-classes"] });
    qc.invalidateQueries({ queryKey: ["student", "calendar"] });
    qc.invalidateQueries({ queryKey: ["student", "balance"] });
  };

  const items = useMemo(() => {
    const out = [];
    (mine ?? []).forEach((c) => {
      let state;
      if (c.status === "UPCOMING") state = "booked";
      else if (c.status === "ATTENDED") state = "attended";
      else if (c.status === "MISSED") state = "missed";
      else return;
      out.push({
        key: `att-${c.id}`,
        attendanceId: c.id,
        planningId: null,
        state,
        title: c.topic || "Clase",
        topicsDetail: c.topics_detail ?? [],
        level: c.level ?? null,
        levelComparison: c.level_comparison ?? null,
        date: c.date, time: c.time, duration: null,
        location: c.location, teacher: c.teacher,
        score: c.score, hours: c.hours, hasRepeats: false,
      });
    });
    (avail ?? []).forEach((ev) => {
      if (ev.already_booked) return;
      out.push({
        key: `plan-${ev.id}`,
        attendanceId: null,
        planningId: ev.id,
        state: "available",
        title: ev.topic || "Clase",
        topicsDetail: ev.topics_detail ?? [],
        level: ev.level ?? null,
        levelComparison: ev.level_comparison ?? null,
        date: ev.date, time: ev.time, duration: ev.duration,
        location: ev.location, teacher: ev.teacher, score: null, hours: null,
        hasRepeats: ev.has_repeats ?? false,
      });
    });
    return out.sort((a, b) =>
      `${a.date}T${a.time || ""}`.localeCompare(`${b.date}T${b.time || ""}`)
    );
  }, [mine, avail]);

  const sedes = useMemo(
    () => [...new Set(items.map((i) => i.location).filter(Boolean))].sort(),
    [items]
  );
  const topics = useMemo(
    () => [...new Set(items.map((i) => i.title).filter(Boolean))].sort(),
    [items]
  );

  const filtered = useMemo(
    () =>
      items.filter(
        (it) =>
          (statusOn[it.state] ?? true) &&
          (!sedeFilter || it.location === sedeFilter) &&
          (!topicFilter || it.title === topicFilter)
      ),
    [items, statusOn, sedeFilter, topicFilter]
  );

  const events = useMemo(
    () =>
      filtered.map((it) => {
        const start = dayjs(`${it.date}T${it.time || "00:00"}`).toDate();
        const end = dayjs(start).add(it.duration || 1, "hour").toDate();
        return { id: it.key, title: it.title, start, end, resource: it };
      }),
    [filtered]
  );

  async function handleBook(planningId) {
    setMsg(null);
    try {
      setChecking(true);
      const { data: check } = await apiClient.get(`/api/v1/calendar/${planningId}/topic-check/`);
      if (check.has_repeats && check.repeated?.length > 0) {
        setPendingEvent({ planningId, topicCheck: check });
      } else {
        await doBook(planningId);
      }
    } catch {
      notifyError("Error al verificar los temas de la clase. Intenta de nuevo.");
    } finally {
      setChecking(false);
    }
  }

  async function doBook(planningId) {
    try {
      await bookMutation.mutateAsync({ planningId });
      setMsg({ type: "success", text: "Clase agendada con éxito." });
      refresh();
    } catch (err) {
      const data = err?.response?.data;
      const fieldError = data?.field_errors ? Object.values(data.field_errors).flat()[0] : null;
      notifyError(
        fieldError ??
          data?.non_field_errors?.[0] ??
          (data?.detail && data.detail !== "Validation failed." ? data.detail : null) ??
          "No se pudo agendar la clase. Puede que ya haya comenzado o no cumpla la política de reserva."
      );
    }
  }

  async function handleConfirmBookWithRepeat() {
    if (!pendingEvent) return;
    const id = pendingEvent.planningId;
    setPendingEvent(null);
    await doBook(id);
  }

  async function handleCancel(attendanceId) {
    setMsg(null);
    setCancellingId(attendanceId);
    try {
      await cancelMutation.mutateAsync(attendanceId);
      setMsg({ type: "success", text: "Clase cancelada." });
      refresh();
    } catch (err) {
      const data = err?.response?.data;
      notifyError(data?.detail ?? data?.non_field_errors?.[0] ?? "No se pudo cancelar la clase.");
    } finally {
      setCancellingId(null);
    }
  }

  const eventPropGetter = (event) => {
    const bg = STATE_MAP[event.resource?.state]?.color ?? "#6c757d";
    return { style: { backgroundColor: bg, borderColor: bg, color: "#fff" } };
  };

  const onSelectEvent = (event) => {
    const it = event.resource;
    if (it.state === "available") handleBook(it.planningId);
    else if (it.state === "booked") {
      if (window.confirm(`¿Cancelar tu clase de "${it.title}" del ${dayjs(it.date).format("D MMM")}?`)) {
        handleCancel(it.attendanceId);
      }
    }
  };

  const toggleStatus = (key) => setStatusOn((s) => ({ ...s, [key]: !s[key] }));

  const busy = l1 || l2;

  // Selectores de sede y tema (E4), reutilizados en panel y barra.
  const dimensionFilters = (
    <>
      <Form.Select
        size="sm"
        value={sedeFilter}
        onChange={(e) => setSedeFilter(e.target.value)}
        aria-label="Filtrar por sede"
        style={{ maxWidth: 220 }}
      >
        <option value="">Todas las sedes</option>
        {sedes.map((s) => <option key={s} value={s}>{s}</option>)}
      </Form.Select>
      <Form.Select
        size="sm"
        value={topicFilter}
        onChange={(e) => setTopicFilter(e.target.value)}
        aria-label="Filtrar por tema"
        style={{ maxWidth: 240 }}
      >
        <option value="">Todos los temas</option>
        {topics.map((t) => <option key={t} value={t}>{t}</option>)}
      </Form.Select>
    </>
  );

  const filterPanel = (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <div className="text-uppercase small text-muted fw-semibold mb-3" style={{ letterSpacing: ".06em" }}>
          Filtrar por estado
        </div>
        <div className="d-flex flex-column align-items-start gap-2 mb-3">
          {STATES.map((s) => (
            <Form.Check
              key={s.key}
              type="checkbox"
              id={`sfilter-${s.key}`}
              checked={statusOn[s.key]}
              onChange={() => toggleStatus(s.key)}
              label={s.label}
              style={{ accentColor: s.color }}
              className="mb-0"
            />
          ))}
        </div>
        <div className="d-flex flex-column gap-2">{dimensionFilters}</div>
      </Card.Body>
    </Card>
  );

  const filterBar = (
    <Card className="border-0 shadow-sm mb-3">
      <Card.Body className="py-3 d-flex align-items-center flex-wrap gap-4">
        <span className="text-uppercase small text-muted fw-semibold me-1" style={{ letterSpacing: ".06em" }}>
          Filtrar por estado
        </span>
        {STATES.map((s) => (
          <label
            key={s.key}
            htmlFor={`sbar-${s.key}`}
            className="d-inline-flex align-items-center gap-2 mb-0"
            style={{ cursor: "pointer" }}
          >
            <input
              id={`sbar-${s.key}`}
              type="checkbox"
              checked={statusOn[s.key]}
              onChange={() => toggleStatus(s.key)}
              style={{ accentColor: "var(--kp-primary)", width: "1.15em", height: "1.15em", cursor: "pointer" }}
            />
            <span>{s.label}</span>
          </label>
        ))}
        <div className="d-flex gap-2 ms-auto flex-wrap">{dimensionFilters}</div>
      </Card.Body>
    </Card>
  );

  const balanceBanner = (
    <Alert
      variant={availableHours <= 0 ? "warning" : "light"}
      className="border d-flex align-items-center gap-2 py-2 mb-3"
    >
      <i className="mdi mdi-wallet-outline" aria-hidden="true" />
      <span>
        Saldo disponible: <strong>{availableHours} h</strong>
        {absenceHours > 0 && (
          <>
            {" "}· <span className="text-danger">Descontadas por inasistencia: {absenceHours} h</span>
          </>
        )}
        {availableHours <= 0 && " — compra más horas para poder agendar nuevas clases."}
      </span>
    </Alert>
  );

  const calendarBlock = (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <ClassesCalendar
          events={events}
          onSelectEvent={onSelectEvent}
          eventPropGetter={eventPropGetter}
          defaultView="month"
        />
        <p className="text-muted small mb-0 mt-2">
          Gris = disponible (clic para agendar) · Navy = agendada (clic para cancelar) ·
          Verde = asistida · <i className="mdi mdi-repeat" aria-hidden="true" /> = repite un tema visto.
        </p>
      </Card.Body>
    </Card>
  );

  const emptyFiltered = (
    <Alert variant="light" className="border text-muted mb-0">
      No hay clases con los filtros seleccionados.
    </Alert>
  );

  const cardList = (colClassName) =>
    filtered.map((it) => (
      <ClassItemCard
        key={it.key}
        item={it}
        onBook={handleBook}
        onCancel={handleCancel}
        cancelling={cancellingId}
        availableHours={availableHours}
        colClassName={colClassName}
      />
    ));

  if (busy) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        Cargando clases…
      </div>
    );
  }
  if (e1 || e2) {
    return <Alert variant="danger" className="m-3">Error al cargar tus clases.</Alert>;
  }

  return (
    <div className="container-fluid px-3">
      <Toaster position="top-right" />
      <div className="d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
        <h4 className="mb-0">Mis clases</h4>
        <ButtonGroup size="sm" aria-label="Cambiar vista">
          {VIEWS.map((v) => (
            <Button
              key={v.key}
              variant={view === v.key ? "primary" : "outline-primary"}
              onClick={() => setView(v.key)}
              title={v.label}
            >
              <i className={`mdi ${v.icon} me-1`} aria-hidden="true" />
              {v.label}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      {balanceBanner}

      {msg && (
        <Alert variant={msg.type} dismissible onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}
      {checking && (
        <p className="text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-1" />
          Verificando temas…
        </p>
      )}

      {items.length === 0 && (
        <Alert variant="info">Aún no tienes clases ni hay disponibles próximamente.</Alert>
      )}

      {items.length > 0 && view === "combined" && (
        <div className="row g-3">
          <div className="col-lg-4">
            {filterPanel}
            <div className="mt-3 pe-1" style={{ maxHeight: 640, overflowY: "auto" }}>
              {filtered.length === 0 ? emptyFiltered : cardList("mb-3")}
            </div>
          </div>
          <div className="col-lg-8">{calendarBlock}</div>
        </div>
      )}

      {items.length > 0 && view === "cards" && (
        <>
          {filterBar}
          {filtered.length === 0 ? emptyFiltered : <div className="row">{cardList()}</div>}
        </>
      )}

      {items.length > 0 && view === "calendar" && (
        <>
          {filterBar}
          {calendarBlock}
        </>
      )}

      {pendingEvent && (
        <TopicRepeatAlert
          topicName={pendingEvent.topicCheck.repeated[0].topic}
          lastDate={pendingEvent.topicCheck.repeated[0].last_seen}
          lastScore={pendingEvent.topicCheck.repeated[0].last_score}
          blocked={pendingEvent.topicCheck.blocked ?? false}
          onConfirm={handleConfirmBookWithRepeat}
          onCancel={() => setPendingEvent(null)}
        />
      )}
    </div>
  );
}

export { MyClassesPage };
